import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Box, Button, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { useState } from "react";
import { ViewToggle } from "../../generic/components/view-toggle";
import { AssetCount } from "../asset-count";
import { AssetGrid } from "../asset-grid";
import { ROOT_ID } from "../utils";

import type { UUID } from "../../../utils/uuid";
import type { ViewMode } from "../../generic/components/view-toggle";
import type { ContentLibraryState } from "../reducer/reducer";
import type { Asset } from "../server/digital-asset-service";
import type { Folder } from "../server/folder-service";
import type { DragEvent, FC, MouseEvent, ReactElement } from "react";

interface ContentLibraryAssetsProps {
  filteredData: Asset[];
  allFilteredData: Asset[];
  state: ContentLibraryState;
  slots: {
    breadcrumb: ReactElement;
    filter?: ReactElement;
    uploadButton?: ReactElement;
    contentTypeChips?: ReactElement;
    searchField: ReactElement;
    tagChips?: ReactElement;
    headerActions?: ReactElement;
  };
  designer?: string | null;
  selectedAsset?: Asset | null;
  onSelectAsset: (asset: Asset, event: MouseEvent, visibleAssets: Asset[]) => void;
  onClearSelection: () => void;
  onArchiveSelectedAssets: () => void;
  onCreateFolder: () => void;
  filteredFolders: Folder[];
  allFilteredFolders: Folder[];
  onArchiveFolder: () => void;
  onMoveFolder: () => void;
  allFolders?: Folder[];
  onClickFolder: (folderId: UUID) => void;
  onPreviewAsset?: (asset: Asset) => void;
  onAssetDragStart?: (asset: Asset, event: DragEvent) => void;
  onFolderDragStart?: (folder: Folder, event: DragEvent) => void;
  onFolderDragOver?: (event: DragEvent) => void;
  onFolderDrop?: (folderId: Folder["id"], event: DragEvent) => void;
  hideFolderActions?: boolean;
}

export const ContentLibraryAssets: FC<ContentLibraryAssetsProps> = ({
  filteredData,
  allFilteredData,
  state,
  slots,
  designer,
  selectedAsset,
  onSelectAsset,
  onClearSelection,
  onArchiveSelectedAssets,
  onCreateFolder,
  filteredFolders,
  allFilteredFolders,
  onArchiveFolder,
  onMoveFolder,
  allFolders,
  onClickFolder,
  onPreviewAsset,
  onAssetDragStart,
  onFolderDragStart,
  onFolderDragOver,
  onFolderDrop,
  hideFolderActions = false,
}) => {
  const [tab, setTab] = useState<"this-folder" | "all-folders">("this-folder");
  const { value: viewMode = "grid", set: setViewMode } = useLocalStorageValue<ViewMode>(
    "content-library-view-mode",
    {
      defaultValue: "grid",
    },
  );

  const inRootFolder = !state.selectedTreeItemId || state.selectedTreeItemId === ROOT_ID;
  const hasSearch = Boolean(state.searchText.trim());
  const hasAppliedFilters = state.appliedTags.length > 0 || state.appliedContentTypes.length > 0;
  const showTabs = hasSearch || hasAppliedFilters;
  const showAllFoldersResults = showTabs && tab === "all-folders";
  const assetsToShow = showAllFoldersResults ? allFilteredData : filteredData;
  const foldersToShow = showAllFoldersResults ? allFilteredFolders : filteredFolders;
  const showFolders = !hasAppliedFilters;
  const visibleThisFolderCount = showFolders ? filteredFolders.length : 0;
  const visibleAllFolderCount = showFolders ? allFilteredFolders.length : 0;
  return (
    <Stack>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        {slots.breadcrumb}
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          {!hideFolderActions && !inRootFolder && (
            <Button
              variant="text"
              color="primary"
              data-analytics-id="content-library-move-folder"
              onClick={onMoveFolder}
            >
              Move folder
            </Button>
          )}
          {!hideFolderActions && (
            <Button
              variant="text"
              color="primary"
              data-analytics-id="content-library-create-sub-folder"
              data-testid="content-library-create-sub-folder"
              onClick={onCreateFolder}
            >
              Create sub-folder
            </Button>
          )}
          {!hideFolderActions && !inRootFolder && (
            <IconButton
              data-testid="archive-folder"
              data-analytics-id="content-library-archive-folder"
              aria-label="Archive folder"
              onClick={onArchiveFolder}
            >
              <DeleteOutlined color="error" sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          {slots.headerActions}
        </Stack>
      </Box>
      <Stack direction="row" sx={{ justifyContent: "space-between", pb: 2 }}>
        <Box sx={{ flexGrow: 1, mr: 2 }}>{slots.searchField}</Box>
        {slots.filter}
        {slots.uploadButton}
      </Stack>
      {showTabs && (
        <Tabs
          value={tab}
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          onChange={(_, nextTab) => setTab(nextTab as "this-folder" | "all-folders")}
          sx={{ mb: 2 }}
        >
          <Tab
            label={`Results in this folder (${filteredData.length + visibleThisFolderCount})`}
            value="this-folder"
          />
          <Tab
            label={`Results in all folders (${allFilteredData.length + visibleAllFolderCount})`}
            value="all-folders"
          />
        </Tabs>
      )}
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <AssetCount
          assets={assetsToShow}
          folders={showFolders ? foldersToShow : []}
          searchText={state.searchText}
        />
        <ViewToggle
          value={viewMode}
          onChange={(nextView) => {
            if (!nextView) return;
            setViewMode(nextView);
          }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.25,
              py: 0.75,
            },
          }}
        />
      </Stack>
      {state.isSelectionMode && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {state.selectedAssetIds.length} selected
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Drag selected assets to a folder or
            </Typography>
            <Button
              variant="text"
              color="error"
              data-analytics-id="content-library-archive-selected-assets"
              onClick={onArchiveSelectedAssets}
              disabled={state.selectedAssetIds.length === 0}
            >
              Archive assets
            </Button>
            <Button
              variant="text"
              data-analytics-id="content-library-clear-selection-bar"
              onClick={onClearSelection}
              disabled={state.selectedAssetIds.length === 0}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      )}
      <Stack sx={{ gap: 1, mb: 2 }}>
        {slots.contentTypeChips}
        {slots.tagChips}
      </Stack>
      <AssetGrid
        allFolders={allFolders}
        assets={assetsToShow}
        selectedAsset={selectedAsset}
        onClick={(asset, event) => onSelectAsset(asset, event, assetsToShow)}
        onPreview={onPreviewAsset}
        selectedAssetIds={state.selectedAssetIds}
        selectionMode={state.isSelectionMode}
        designer={designer}
        folders={foldersToShow}
        showFolderNames={showAllFoldersResults}
        showFolders={showFolders}
        onClickFolder={onClickFolder}
        onAssetDragStart={onAssetDragStart}
        onFolderDragStart={onFolderDragStart}
        onFolderDragOver={onFolderDragOver}
        onFolderDrop={onFolderDrop}
        view={viewMode}
      />
    </Stack>
  );
};
